// frontend/src/pages/LandingPage/DynamicHeroImage.jsx
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import faceImage from './assets/hero-face.webp';
import shieldLogo from './assets/vetraai_logo.webp'; 

const COLORS = {
  blue: 'var(--secondary-color)', 
  red: '#F44336',  
  grid: 'rgba(255, 255, 255, 0.15)',
  text: '--text-primary-color-white',
  tooltipBg: 'var(--primary-color) !important'
};

const PATHS = {
  shield: "M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z",
  danger: "M15.73 3H8.27L3 8.27v7.46L8.27 21h7.46L21 15.73V8.27L15.73 3zM12 17.3c-.72 0-1.3-.58-1.3-1.3 0-.72.58-1.3 1.3-1.3.72 0 1.3.58 1.3 1.3 0 .72-.58 1.3-1.3 1.3zm1-4.3h-2V7h2v6z",
  info: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
};

const SCAN_DURATION = 5; 
const PAUSE_DURATION = 2000; 
const SVG_HEIGHT = 320; 
const VERIFY_LINK = "https://www.vetra-ai.com/verify?id=8abfcd4b52015d38fb7b2c06bffd2426793075d569e0051d4e2254144206f810&ref=c8003531-004f-43aa-8b9b-5b7e4ae5c5e9&context=ai_detection&context=fake_news_detection&regions=true&explanation=true";

const DynamicHeroImage = () => {
  const { t } = useTranslation();
  const [isHovering, setIsHovering] = useState(false);
  const isHoveringRef = useRef(false); 
  
  const [loopKey, setLoopKey] = useState(0); 
  const [scanFinished, setScanFinished] = useState(false); 
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [, setIsShieldClicked] = useState(false);
  const [isHoveringShield, setIsHoveringShield] = useState(false);
  const [showShieldTooltip, setShowShieldTooltip] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize(); 
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // --- ⚙️ TOOLTIP CONFIGURATION ---
  // You now have total control here. Adjust 'left', 'top', 'transform', and 'arrow' to shift boxes around.
  // The 'arrowOffset' perfectly aligns the arrow with the anchor when you apply a custom transform offset.
  const DETECTED_REGIONS = [
    {
        id: 'eyes',
        x: 350, y: 138, width: 100, height: 25,
        reason: t('landingPage.heroImage.tooltips.eyes'),
        severity: 6,
        desktop: { left: '35%', top: '-10%', transform: 'translate(0, 0)', marginTop: '-12px', arrow: 'bottom', arrowOffset: '50%' },
        mobile: { left: '50%', top: '51%', transform: 'none', marginLeft: '-100px', marginTop: '10px', arrow: 'top', arrowOffset: '50%' }
    },
    {
        id: 'skin',
        x: 420, y: 175, width: 40, height: 45,
        reason: t('landingPage.heroImage.tooltips.skin'),
        severity: 8,
        desktop: { left: '40%', top: '68%', transform: 'translate(0, 0)', marginTop: '12px', arrow: 'top', arrowOffset: '50%' },
        mobile: { left: '55%', top: '68.75%', transform: 'none', marginLeft: '-100px', marginTop: '10px', arrow: 'top', arrowOffset: '50%' }
    },
    {
        id: 'hair',
        x: 333, y: 30, width: 130, height: 30,
        reason: t('landingPage.heroImage.tooltips.hair'),
        severity: 5,
        desktop: { left: '58%', top: '-14%', transform: 'translate(0, 0)', marginLeft: '12px', arrow: 'left', arrowOffset: '50%' },
        mobile: { left: '49.75%', top: '18.75%', transform: 'none', marginLeft: '-100px', marginTop: '10px', arrow: 'top', arrowOffset: '50%' }
    }
  ];

  const INFO_POINT = {
    id: 'info',
    x: 490, y: 255, 
    title: t('landingPage.heroImage.info.title'),
    desktop: { left: '63%', top: '-12%', transform: 'translate(0, 0)', marginLeft: '12px', width: '290px', arrow: 'left', arrowOffset: '50%' },
    mobile: { left: '61%', top: '83.75%', transform: 'none', marginLeft: '-100px', marginTop: '10px', arrow: 'top', arrowOffset: '50%' },
    reason: (
      <ul style={{ paddingLeft: '14px', marginBottom: '10px', listStyleType: 'disc', textAlign: 'left' }}>
        <li style={{marginBottom: '4px'}}><strong>{t('landingPage.heroImage.info.skinTitle')}</strong> {t('landingPage.heroImage.info.skinDesc')}</li>
        <li style={{marginBottom: '4px'}}><strong>{t('landingPage.heroImage.info.lightingTitle')}</strong> {t('landingPage.heroImage.info.lightingDesc')}</li>
        <li style={{marginBottom: '4px'}}><strong>{t('landingPage.heroImage.info.backgroundTitle')}</strong> {t('landingPage.heroImage.info.backgroundDesc')}</li>
        <li><strong>{t('landingPage.heroImage.info.genericTitle')}</strong> {t('landingPage.heroImage.info.genericDesc')}</li>
      </ul>
    )
  };
  // --------------------------------

  const handleAnimationComplete = () => {
    if (isHoveringRef.current) {
      setScanFinished(true); 
    } else {
      setTimeout(() => {
        if (isHoveringRef.current) setScanFinished(true); 
        else setLoopKey(prev => prev + 1); 
      }, PAUSE_DURATION);
    }
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
    isHoveringRef.current = true;
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    isHoveringRef.current = false;
    setActiveTooltip(null);
    if (scanFinished) {
      setScanFinished(false);
      setLoopKey(prev => prev + 1);
    }
  };

  const handleShieldClick = () => {
      setIsShieldClicked(true);
      window.open(VERIFY_LINK, '_blank');
  };

  const getTooltipStyle = (region) => {
    const config = isMobile ? region.mobile : region.desktop;
    const width = config.width || (isMobile ? '200px' : '180px');

    return {
      position: 'absolute',
      backgroundColor: '#171F29', 
      color: 'white',
      padding: '8px 10px',
      borderRadius: '6px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.9)', 
      border: `1px solid ${COLORS.blue}`,
      zIndex: 10000,
      pointerEvents: 'none',
      lineHeight: '1.3',
      textAlign: 'left',
      fontSize: isMobile ? '0.65rem' : '0.7rem',
      // Dynamically load positions directly from the config block
      width: width,
      left: config.left,
      top: config.top,
      transform: config.transform,
      marginTop: config.marginTop || '0px',
      marginLeft: config.marginLeft || '0px'
    };
  };

  const getArrowStyle = (region) => {
      const config = isMobile ? region.mobile : region.desktop;
      const arrowType = config.arrow;
      const offset = config.arrowOffset || '50%';

      const baseArrow = {
        position: 'absolute', 
        width: '10px', height: '10px', 
        backgroundColor: COLORS.tooltipBg,
        transform: 'rotate(45deg)',
      };

      // Attaches arrows based on config strings
      switch(arrowType) {
          case 'bottom': return { ...baseArrow, bottom: '-6px', left: offset, marginLeft: '-5px', borderRight: `1px solid ${COLORS.blue}`, borderBottom: `1px solid ${COLORS.blue}` };
          case 'top': return { ...baseArrow, top: '-6px', left: offset, marginLeft: '-5px', borderTop: `1px solid ${COLORS.blue}`, borderLeft: `1px solid ${COLORS.blue}` };
          case 'left': return { ...baseArrow, left: '-6px', top: offset, marginTop: '-5px', borderBottom: `1px solid ${COLORS.blue}`, borderLeft: `1px solid ${COLORS.blue}` };
          case 'right': return { ...baseArrow, right: '-6px', top: offset, marginTop: '-5px', borderTop: `1px solid ${COLORS.blue}`, borderRight: `1px solid ${COLORS.blue}` };
          default: return baseArrow;
      }
  };

  return (
    <div 
      className="hero-visual-card hero-visual-card--interactive"
      style={{ width: '100%', maxWidth: '600px', margin: '0 auto', zIndex: 10 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div style={{ position: 'relative', width: '100%', aspectRatio: '800 / 320' }}>
          
        <svg viewBox={`0 0 800 ${SVG_HEIGHT}`} fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
            {/* SVG Definitions */}
            <defs>
            <filter id="prescan-dark"><feColorMatrix type="matrix" values="0.05 0.05 0.05 0 0 0.05 0.05 0.05 0 0 0.05 0.05 0.05 0 0 0 0 0 1 0" /></filter>
            <filter id="postscan-light"><feColorMatrix type="matrix" values="0.33 0.33 0.33 0 0 0.33 0.33 0.33 0 0 0.33 0.33 0.33 0 0 0 0 0 1 0" /></filter>
            <filter id="dark-and-blur">
                <feColorMatrix type="matrix" values="0.05 0.05 0.05 0 0 0.05 0.05 0.05 0 0 0.05 0.05 0.05 0 0 0 0 0 1 0" result="darkened" />
                <feGaussianBlur in="darkened" stdDeviation="4" />
            </filter>

            <clipPath id="scan-clip">
                <motion.rect key={loopKey} x="280" y="20" width="240" initial={{ height: 0 }} animate={{ height: 280 }} transition={{ duration: SCAN_DURATION, ease: "easeInOut" }} onAnimationComplete={handleAnimationComplete} />
            </clipPath>

            <clipPath id="chart-fill-clip">
                <motion.rect x="580" y="0" height="320" initial={{ width: 0 }} animate={{ width: 180 }} transition={{ duration: 2.5, ease: "easeIn", delay: 0.8 }} />
            </clipPath>

            <mask id="focus-mask">
                <rect x="0" y="0" width="800" height="320" fill={activeTooltip && activeTooltip.id !== 'info' ? "black" : "white"} />
                {activeTooltip && activeTooltip.id !== 'info' && (<rect x={activeTooltip.x} y={activeTooltip.y} width={activeTooltip.width} height={activeTooltip.height} fill="white" />)}
            </mask>

            <mask id="grid-fade">
                <linearGradient id="grid-grad" x1="0" y1="0" x2="1" y2="0"><stop offset="0.5" stopColor="black" /><stop offset="0.9" stopColor="white" /></linearGradient>
                <rect x="0" y="0" width="800" height="320" fill="url(#grid-grad)" />
            </mask>

            <linearGradient id="chart-fill-fade" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={COLORS.red} stopOpacity="0.3"/><stop offset="100%" stopColor={COLORS.red} stopOpacity="0"/></linearGradient>
            <linearGradient id="scanner-grad" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor={COLORS.blue} stopOpacity="0"/><stop offset="50%" stopColor={COLORS.blue} stopOpacity="1"/><stop offset="100%" stopColor={COLORS.blue} stopOpacity="0"/></linearGradient>
            </defs>

            {/* SVG Elements */}
            <rect x="0" y="0" width="800" height="320" rx="16" fill="rgba(255, 255, 255, 0.08)" stroke={isHovering ? COLORS.red : COLORS.blue} strokeWidth="5" style={{transition: 'stroke 1s ease'}} />
            
            <g mask="url(#grid-fade)">
                {[60, 120, 180, 240, 300].map(y => (<line key={'h'+y} x1="500" y1={y} x2="780" y2={y} stroke={COLORS.grid} strokeWidth="0.5" />))}
                {[550, 650, 750].map(x => (<line key={'v'+x} x1={x} y1="20" x2={x} y2="300" stroke={COLORS.grid} strokeWidth="0.5" />))}
            </g>

            <text x="30" y="140" fill="white" fontFamily="'Roboto', Arial, sans-serif" fontSize="18" fontWeight="700" letterSpacing="1">{t('landingPage.heroImage.text1')}</text>
            <text x="30" y="170" fill="white" fontFamily="'Roboto', Arial, sans-serif" fontSize="18" fontWeight="700" letterSpacing="1">{t('landingPage.heroImage.text2')}</text>
            <text x="30" y="200" fill="white" fontFamily="'Roboto', Arial, sans-serif" fontSize="18" fontWeight="700" letterSpacing="1">{t('landingPage.heroImage.text3')}</text>
            <motion.text x="30" y="270" fill={COLORS.red} fontFamily="'Roboto', sans-serif" fontSize="60" fontWeight="900" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1, duration: 0.5 }} style={{ filter: `drop-shadow(0 0 8px ${COLORS.red})` }}>{t('landingPage.heroImage.stat')}</motion.text>

            <image href={faceImage} x="280" y="20" width="240" height="280" preserveAspectRatio="xMidYMid slice" filter="url(#prescan-dark)" opacity="0.95" />

            <g clipPath="url(#scan-clip)">
                <image href={faceImage} x="280" y="20" width="240" height="280" preserveAspectRatio="xMidYMid slice" filter="url(#dark-and-blur)" />
                <image href={faceImage} x="280" y="20" width="240" height="280" preserveAspectRatio="xMidYMid slice" filter="url(#postscan-light)" mask="url(#focus-mask)" />

                {DETECTED_REGIONS.map((region, i) => (
                    <motion.rect 
                        key={`${region.id}-${loopKey}`} x={region.x} y={region.y} width={region.width} height={region.height} 
                        fill="rgba(255, 0, 60, 0.1)" stroke={COLORS.red} strokeWidth="2" rx="4"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: (region.y / SVG_HEIGHT) * SCAN_DURATION }} 
                        onMouseEnter={() => setActiveTooltip(region)} onMouseLeave={() => setActiveTooltip(null)}
                        style={{ cursor: 'help', pointerEvents: 'all', opacity: activeTooltip && activeTooltip.id !== region.id && activeTooltip.id !== 'info' ? 0 : 1 }}
                    />
                ))}

                <motion.foreignObject 
                    x="280" y="20" width="40" height="40" style={{ cursor: 'pointer', overflow: 'visible' }}
                    initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: (260 / SVG_HEIGHT) * SCAN_DURATION }}
                >
                    <img 
                        src={shieldLogo} alt="Verify" onClick={handleShieldClick}
                        onMouseEnter={() => { setShowShieldTooltip(true); setIsHoveringShield(true); }}
                        onMouseLeave={() => { setShowShieldTooltip(false); setIsHoveringShield(false); }}
                        style={{ width: '100%', height: '100%', filter: isHoveringShield ? 'brightness(1.5) grayscale(100%)' : 'brightness(0.7) grayscale(100%)', transition: 'filter 0.3s ease' }}
                    />
                </motion.foreignObject>
            </g>

            <motion.g
                key={`info-${loopKey}`} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: SCAN_DURATION * 0.75, type: 'spring' }}
                onMouseEnter={() => setActiveTooltip(INFO_POINT)} onMouseLeave={() => setActiveTooltip(null)} style={{ cursor: 'pointer' }}
            >
                <circle cx={INFO_POINT.x} cy={INFO_POINT.y} r="12" fill={COLORS.blue} stroke="white" strokeWidth="2" />
                <path transform={`translate(${INFO_POINT.x - 12}, ${INFO_POINT.y - 12})`} d={PATHS.info} fill="white" scale="0.8" />
                <motion.circle cx={INFO_POINT.x} cy={INFO_POINT.y} r="12" fill="transparent" stroke={COLORS.blue} strokeWidth="2" animate={{ r: [12, 20], opacity: [1, 0] }} transition={{ repeat: Infinity, duration: 1.5 }} />
            </motion.g>

            <motion.rect key={`line-${loopKey}`} x="280" y="0" width="240" height="3" fill="url(#scanner-grad)" initial={{ y: 20, opacity: 0 }} animate={{ y: 300, opacity: [0, 1, 1, 0] }} transition={{ duration: SCAN_DURATION, ease: "easeInOut" }} style={{ filter: `drop-shadow(0 0 4px ${COLORS.blue})` }} />

            <motion.path d="M 580 250 L 610 235 L 640 255 L 670 230 L 700 240 L 730 215 L 760 205" fill="none" stroke={COLORS.blue} strokeWidth="2" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, delay: 0.5, ease: "linear" }} />
            <motion.g initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 2.5 }}>
                <path transform="translate(760, 190) scale(1.2)" d={PATHS.shield} fill={COLORS.blue} stroke={COLORS.blue} strokeWidth="1.5" />
            </motion.g>

            <motion.path d="M 580 280 L 610 270 L 640 200 L 670 220 L 700 100 L 730 120 L 760 40" fill="none" stroke={COLORS.red} strokeWidth="3" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2.5, ease: "easeIn", delay: 0.8 }} />
            <motion.path d="M 580 280 L 610 270 L 640 200 L 670 220 L 700 100 L 730 120 L 760 40 L 760 300 L 580 300 Z" fill="url(#chart-fill-fade)" clipPath="url(#chart-fill-clip)" />
            <motion.g initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 3.3, type: "spring" }}>
                <path transform="translate(760, 20) scale(1.2)" d={PATHS.danger} fill={COLORS.red} />
            </motion.g>
        </svg>

        {/* --- HTML OVERLAYS --- */}
        <AnimatePresence>
            {activeTooltip && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.15 }}
                    style={getTooltipStyle(activeTooltip)}
                >
                    <div style={{ fontWeight: '700', marginBottom: '4px', color: COLORS.red, fontSize: '0.7rem', letterSpacing: '0.5px', textTransform: 'uppercase', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        {activeTooltip.id === 'info' ? activeTooltip.title : `${t('landingPage.heroImage.tooltips.severity')}: ${activeTooltip.severity}/10`}
                    </div>
                    <div style={{color: '#cbd5e1', fontSize: '0.75rem'}}>
                        {activeTooltip.reason}
                    </div>
                    <div style={getArrowStyle(activeTooltip)}></div>
                </motion.div>
            )}
            
            {showShieldTooltip && (
                <motion.div
                    initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 5 }} exit={{ opacity: 0 }}
                    style={{
                        position: 'absolute',
                        left: isMobile ? '16.3%' : '27%', 
                        top: isMobile ? '7.5%' : '21%', 
                        transform: isMobile ? 'translate(-50%, 0)' : 'translateX(-15%)',
                        marginTop: isMobile ? '25px' : '0px',
                        backgroundColor: COLORS.tooltipBg, color: 'white', padding: '8px 12px', borderRadius: '6px',
                        border: `1px solid ${COLORS.blue}`, boxShadow: '0 4px 20px rgba(0,0,0,0.8)',
                        fontSize: '0.7rem', pointerEvents: 'none', zIndex: 40, 
                        
                        // Fix overflow for shield tooltip on small phones:
                        whiteSpace: isMobile ? 'normal' : 'nowrap',
                        width: isMobile ? '180px' : 'auto',
                        textAlign: 'center'
                    }}
                >
                    {t('landingPage.heroImage.info.verificationDetails')}
                    <div style={{
                        position: 'absolute', top: '-6px', left: isMobile ? '50%' : '45%', 
                        marginLeft: isMobile ? '-5px' : '0px', 
                        transform: 'rotate(45deg)',
                        width: '10px', height: '10px', backgroundColor: COLORS.tooltipBg,
                        borderLeft: `1px solid ${COLORS.blue}`, borderTop: `1px solid ${COLORS.blue}`
                    }}></div>
                </motion.div>
            )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DynamicHeroImage;